/**
 * Priority Service
 */

import { api } from '../../lib/api'
import { API_ROUTES } from '../../config/constants'
import type {
  Priority,
  CreatePriorityData,
  UpdatePriorityData,
  PriorityWithCount,
} from '../../domain/priority/types'

export const priorityService = {
  /**
   * List all priorities
   */
  list: async (options?: {
    includeInactive?: boolean
    withCount?: boolean
  }): Promise<{ priorities: Priority[] | PriorityWithCount[] }> => {
    const params = new URLSearchParams()
    if (options?.includeInactive) params.set('includeInactive', 'true')
    if (options?.withCount) params.set('withCount', 'true')

    const url = params.toString()
      ? `${API_ROUTES.PRIORITIES.BASE}?${params.toString()}`
      : API_ROUTES.PRIORITIES.BASE

    const response = await api.get(url)
    return response.data
  },

  /**
   * Get priority by ID
   */
  getById: async (id: string): Promise<Priority> => {
    const response = await api.get(API_ROUTES.PRIORITIES.BY_ID(id))
    return response.data
  },

  /**
   * Get default priority
   */
  getDefault: async (): Promise<Priority | null> => {
    try {
      const response = await api.get(API_ROUTES.PRIORITIES.DEFAULT)
      return response.data
    } catch (error: any) {
      if (error?.response?.status === 404) {
        return null
      }
      throw error
    }
  },

  /**
   * Create new priority
   */
  create: async (data: CreatePriorityData): Promise<Priority> => {
    const response = await api.post(API_ROUTES.PRIORITIES.BASE, data)
    return response.data
  },

  /**
   * Update priority
   * Using POST instead of PUT for WAF compatibility
   */
  update: async (id: string, data: UpdatePriorityData): Promise<Priority> => {
    const response = await api.post(API_ROUTES.PRIORITIES.UPDATE(id), data)
    return response.data
  },

  /**
   * Delete priority
   * Using POST instead of DELETE for WAF compatibility
   */
  delete: async (id: string): Promise<void> => {
    await api.post(API_ROUTES.PRIORITIES.DELETE(id))
  },

  /**
   * Set priority as default
   */
  setDefault: async (id: string): Promise<Priority> => {
    const response = await api.post(API_ROUTES.PRIORITIES.SET_DEFAULT(id))
    return response.data
  },
}
